<div class="modal-header">
  <h4 class="modal-title">Membership Details</h4>
  <button type="button" class="close" aria-label="Close" (click)="activeModal.dismiss()">
    <span aria-hidden="true">&times;</span>
  </button>
</div>

<div class="modal-body">

  <div class="mb-3">
    <div><strong>Membership:</strong> {{ membership.name }}</div>
    <div *ngIf="membership.rolling_event_type_roster_name">
      <strong>Roster:</strong> {{ membership.rolling_event_type_roster_name }}
    </div>
    <div>
      <strong>Owner:</strong>
      {{ membership.owner_first_name }} {{ membership.owner_last_name }}
    </div>
  </div>

  <div class="mb-3">
    <div class="mb-1"><strong>Members</strong></div>
    <ul class="members-list" *ngIf="members.length > 0; else noMembers">
      <li *ngFor="let member of members">
        <a [routerLink]="['/customers', member.id, 'memberships-and-passes']"
           target="_blank" rel="noopener">
          {{ member.name }}
        </a>
        <span class="text-muted" *ngIf="member.isUnlinked">(pending)</span>
        <ul *ngIf="member.details.length > 0">
          <li *ngFor="let detail of member.details">{{ detail }}</li>
        </ul>
      </li>
    </ul>
    <ng-template #noMembers>
      <div class="text-muted">No members</div>
    </ng-template>
  </div>

  <dl class="row mb-0 details-list">
    <dt class="col-sm-5">Status</dt>
    <dd class="col-sm-7">
      <app-membership-status-badge
        [status]="membership.status"
        [isPendingStaffApproval]="membership.is_pending_staff_approval">
      </app-membership-status-badge>
    </dd>

    <dt class="col-sm-5">Payment type</dt>
    <dd class="col-sm-7">{{ getMembershipPurchaseTypeDisplay(membership.purchase_type) }}</dd>

    <ng-container *ngIf="membership.event_granting_benefit_id">
      <dt class="col-sm-5">Granted by</dt>
      <dd class="col-sm-7">
        {{ membership.event_granting_benefit_name }}
        <ng-container *ngIf="membership.event_granting_benefit_first_start_datetime">
          on {{ dateStringFromISO8601(membership.event_granting_benefit_first_start_datetime) }}
        </ng-container>
        <ng-container *ngIf="membership.event_granting_benefit_reservation_id">
          <br>
          <a [routerLink]="['/customers', membership.owner_id, 'reservations']"
             [queryParams]="reservationQueryParams"
             target="_blank" rel="noopener">View Reservation</a>
        </ng-container>
      </dd>
    </ng-container>

    <dt class="col-sm-5">Length</dt>
    <dd class="col-sm-7">{{ lengthDisplay }}</dd>

    <dt class="col-sm-5">Initially purchased</dt>
    <dd class="col-sm-7">
      <ng-container *ngIf="purchasedDate; else notAvailable">
        {{ dateStringFromISO8601(purchasedDate) }}
      </ng-container>
    </dd>

    <dt class="col-sm-5">Original start date</dt>
    <dd class="col-sm-7">
      <ng-container *ngIf="membership.start_date; else notAvailable">
        {{ dateStringFromISO8601(membership.start_date) }}
      </ng-container>
    </dd>

    <ng-container *ngIf="paidUpfront">
      <dt class="col-sm-5">Paid upfront</dt>
      <dd class="col-sm-7">
        {{ membership.number_of_upfront_periods_paid }}
        {{ membership.number_of_upfront_periods_paid === 1 ? 'period' : 'periods' }}
      </dd>
    </ng-container>

    <ng-container *ngIf="showNextBill">
      <dt class="col-sm-5">Next bill date</dt>
      <dd class="col-sm-7">{{ dateStringFromISO8601(membership.end_date) }}</dd>

      <dt class="col-sm-5">Next bill amount</dt>
      <dd class="col-sm-7">{{ orgConfig.formatCurrency(membership.billing_amount) }}</dd>
    </ng-container>

    <ng-container *ngIf="showEndDate">
      <dt class="col-sm-5">End date</dt>
      <dd class="col-sm-7">{{ dateStringFromISO8601(membership.end_date) }}</dd>
    </ng-container>

    <ng-container *ngIf="hasRecurringDiscount">
      <dt class="col-sm-5">Recurring discount</dt>
      <dd class="col-sm-7">
        <ng-container *ngIf="membership.recurring_discount_type === MembershipDiscountType.currency">
          {{ orgConfig.formatCurrency(membership.recurring_discount_amount) }}
        </ng-container>
        <ng-container *ngIf="membership.recurring_discount_type === MembershipDiscountType.percentage">
          {{ membership.recurring_discount_amount }}%
        </ng-container>
        <span *ngIf="recurringDiscountName">({{ recurringDiscountName }})</span>
      </dd>
    </ng-container>

    <ng-container *ngIf="hasOneOffDiscount">
      <dt class="col-sm-5">One-time discount</dt>
      <dd class="col-sm-7">
        <ng-container *ngIf="membership.one_off_discount_type === MembershipDiscountType.currency">
          {{ orgConfig.formatCurrency(membership.one_off_discount_amount) }}
        </ng-container>
        <ng-container *ngIf="membership.one_off_discount_type === MembershipDiscountType.percentage">
          {{ membership.one_off_discount_amount }}%
        </ng-container>
        <span *ngIf="membership.one_off_discount_name">({{ membership.one_off_discount_name }})</span>
      </dd>
    </ng-container>

    <ng-container *ngIf="membership.membership_cancel_date">
      <dt class="col-sm-5">Scheduled to end</dt>
      <dd class="col-sm-7">{{ dateStringFromISO8601(membership.membership_cancel_date) }}</dd>
    </ng-container>

    <ng-container *ngIf="membership.membership_freeze_date">
      <dt class="col-sm-5">Scheduled to freeze</dt>
      <dd class="col-sm-7">{{ dateStringFromISO8601(membership.membership_freeze_date) }}</dd>
    </ng-container>

    <ng-container *ngIf="membership.membership_unfreeze_date">
      <dt class="col-sm-5">Scheduled to unfreeze</dt>
      <dd class="col-sm-7">{{ dateStringFromISO8601(membership.membership_unfreeze_date) }}</dd>
    </ng-container>

    <dt class="col-sm-5">Total paid to date</dt>
    <dd class="col-sm-7">{{ orgConfig.formatCurrency(membership.total_paid_to_date) }}</dd>

    <ng-container *ngIf="hasRefund">
      <dt class="col-sm-5">Total refunded</dt>
      <dd class="col-sm-7">{{ orgConfig.formatCurrency(membership.total_refunded_to_date) }}</dd>
    </ng-container>
  </dl>

  <hr>

  <h6>Terms &amp; Conditions agreed at purchase</h6>

  <ng-container *ngIf="terms.agreed; else noTerms">
    <div class="terms-text" [innerHtml]="renderedTermsHtml"></div>
    <button *ngIf="termsAreTruncatable"
            type="button"
            class="btn btn-link btn-sm px-0"
            (click)="showFullTerms = !showFullTerms">
      {{ showFullTerms ? 'View Less' : 'View More' }}
    </button>
  </ng-container>

  <ng-template #noTerms>
    <p class="text-muted mb-0" [ngSwitch]="terms.reason">
      <ng-container *ngSwitchCase="MembershipTermsAndConditionsReason.migrated">
        This membership was migrated from other software, so no terms &amp; conditions were agreed to in Capitan.
      </ng-container>
      <ng-container *ngSwitchCase="MembershipTermsAndConditionsReason.benefit">
        This is a benefit membership, so no terms &amp; conditions were agreed to.
      </ng-container>
      <ng-container *ngSwitchCase="MembershipTermsAndConditionsReason.noneConfigured">
        No terms &amp; conditions were configured at the time this membership was purchased.
      </ng-container>
      <ng-container *ngSwitchDefault>
        No terms &amp; conditions were agreed to.
      </ng-container>
    </p>
  </ng-template>

  <ng-template #notAvailable>
    <span class="text-muted">Not available</span>
  </ng-template>

</div>

<div class="modal-footer">
  <button type="button" class="btn btn-primary mr-auto"
          *ngIf="manageCallback"
          (click)="handleManage()">
    Manage
  </button>
  <button type="button" class="btn btn-secondary" (click)="activeModal.dismiss()">
    Close
  </button>
</div>
